/**
 * Path Router
 *
 * Intelligent routing of paths to appropriate picker components.
 * Determines which paths belong to which entity type (audience, task, etc.)
 * and validates path format.
 */

import { parsePath, validatePathFormat, type EntityType } from './pathParser';

/**
 * Invalid path information
 */
export interface InvalidPath {
  /** The invalid path string */
  path: string;

  /** Reason why the path is invalid */
  reason: string;
}

/**
 * Routed paths result
 */
export interface RoutedPaths {
  /** Paths for audience picker */
  audiences: string[];

  /** Paths for task picker */
  tasks: string[];

  /** Paths for variable picker */
  variables: string[];

  /** Paths for the insight-derived variable picker (Task 9, `@insightVariable[...]`) */
  insightVariables: string[];

  /** Paths for social proof picker (reviews, ratings, studyCases, bigNumbers) */
  socialProofs: string[];

  /** Paths for insights picker (aggregated JTBD analyses) */
  insights: string[];

  /** Invalid paths that couldn't be routed */
  invalid: InvalidPath[];
}

/**
 * Detects the type of entity a path points to
 *
 * @param path - Path string
 * @returns Entity type or 'unknown'
 */
export function detectPathType(path: string): EntityType {
  const parsed = parsePath(path);
  return parsed?.entityType || 'unknown';
}

/**
 * Routes an array of paths to their respective entity types
 *
 * This is the main routing function used by CompositeDataPicker.
 * It separates paths by entity type and collects invalid paths for logging.
 *
 * @param paths - Array of path strings
 * @returns Routed paths object with separated arrays
 */
export function routePaths(paths: string[]): RoutedPaths {
  const result: RoutedPaths = {
    audiences: [],
    tasks: [],
    variables: [],
    insightVariables: [],
    socialProofs: [],
    insights: [],
    invalid: [],
  };

  for (const path of paths) {
    // Validate format first
    const validation = validatePathFormat(path);
    if (!validation.valid) {
      result.invalid.push({
        path,
        reason: validation.reason || 'Invalid format',
      });
      continue;
    }

    // Detect entity type
    const entityType = detectPathType(path);

    switch (entityType) {
      case 'audience':
        result.audiences.push(path);
        break;

      case 'task':
        result.tasks.push(path);
        break;

      case 'variable':
        result.variables.push(path);
        break;

      case 'insightVariable':
        result.insightVariables.push(path);
        break;

      case 'review':
      case 'rating':
      case 'studyCase':
      case 'bigNumber':
        result.socialProofs.push(path);
        break;

      case 'insightsAnalysis':
        result.insights.push(path);
        break;

      case 'unknown':
        result.invalid.push({
          path,
          reason: 'Unknown entity type',
        });
        break;

      default:
        result.invalid.push({
          path,
          reason: `Unsupported entity type: ${entityType}`,
        });
    }
  }

  return result;
}

/**
 * Validates a single path
 *
 * @param path - Path string to validate
 * @returns Validation result with details
 */
export function validatePath(path: string): {
  valid: boolean;
  entityType?: EntityType;
  reason?: string;
} {
  const formatValidation = validatePathFormat(path);
  if (!formatValidation.valid) {
    return formatValidation;
  }

  const entityType = detectPathType(path);
  if (entityType === 'unknown') {
    return {
      valid: false,
      reason: 'Unknown entity type',
    };
  }

  return {
    valid: true,
    entityType,
  };
}

/**
 * Filters out invalid paths and logs them
 *
 * @param paths - Array of paths to filter
 * @param onInvalid - Optional callback for invalid paths
 * @returns Array of valid paths only
 */
export function filterValidPaths(
  paths: string[],
  onInvalid?: (invalid: InvalidPath) => void
): string[] {
  const validPaths: string[] = [];

  for (const path of paths) {
    const validation = validatePath(path);
    if (validation.valid) {
      validPaths.push(path);
    } else {
      const invalidPath: InvalidPath = {
        path,
        reason: validation.reason || 'Invalid path',
      };

      onInvalid?.(invalidPath);
    }
  }

  return validPaths;
}

/**
 * Groups paths by entity type
 *
 * @param paths - Array of paths
 * @returns Map of entity type to paths
 */
export function groupPathsByType(paths: string[]): Map<EntityType, string[]> {
  const grouped = new Map<EntityType, string[]>();

  for (const path of paths) {
    const entityType = detectPathType(path);
    if (entityType !== 'unknown') {
      if (!grouped.has(entityType)) {
        grouped.set(entityType, []);
      }
      grouped.get(entityType)!.push(path);
    }
  }

  return grouped;
}
